import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { showNotification } from 'react-admin';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { CircularProgress, IconButton, TextField } from '@material-ui/core';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { createNewSharingUrl } from '../../../../../services/file-explorer/fileExplorerApi';
import FileExplorerConfig from '../../../../../../../../npm-packages/authentication/src/config/FileExplorerConfig';

const useStyles = makeStyles(
{
    fullWidth: {
        width: '100%',
    },
});

const timeUnits = [
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year'
];

const defaultTimeUnit = FileExplorerConfig.shareLinkDialogDefaultTimeUnit;
const defaultTimeQuantity = FileExplorerConfig.shareLinkDialogDefaultTimeQuantity;

function ShareLinkDialog(props) {
    const { open, isDirectory, fileSystemNodePath, handleCancel, showNotification } = props;

    const classes = useStyles();
    const translate = useTranslate();
    const urlInputRef = useRef();

    const [url, setUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(yup.object().shape({
            expiration_time_quantity: yup.number(translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.expiration-time-quantity-is-required`))
                .integer(translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.expiration-time-quantity-is-required`))
                .typeError(translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.expiration-time-quantity-is-required`))
                .min(1, translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.expiration-time-quantity-is-required`))
                .max(1000, translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.expiration-time-quantity-cannot-exceed-maximum`).replace('{max}', 1000)),
        })),
        defaultValues: {
            expiration_time_unit: defaultTimeUnit,
            expiration_time_quantity: defaultTimeQuantity,
        },
        criteriaMode: 'all',
    });

    const startCreateNewSharingUrl = useCallback((data) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);

        const cancellationTokenSource = axios.CancelToken.source();
        createNewSharingUrl(fileSystemNodePath, data.expiration_time_quantity, data.expiration_time_unit, cancellationTokenSource.token)
        .then((response) => {
            setUrl(response.url);
            setIsLoading(false);
        }).catch((error) => {
            showNotification(`cmss.resources.file-explorer.directory-actions-menu.share-link.shared-link-creation-unsuccessful`, 'warning');
            setIsLoading(false);
            if (axios.isCancel(error)) {
                return;
            }
        });
    
        return () => {
            cancellationTokenSource.cancel();
        }
    }, [isLoading, fileSystemNodePath]);


    function resetDialog() {
        reset();
        setIsLoading(false);
        setUrl(null);
    }

    function handleOnClickCreateLinkButton(data) {
        startCreateNewSharingUrl(data);
    }

    function handleCancelInternal() {
        handleCancel();
    }

    const handleOnClickCopyLinkButton = () => {
        urlInputRef.current.select();
        navigator.clipboard.writeText(url);
    }

    const name = fileSystemNodePath.length > 0 ? fileSystemNodePath[fileSystemNodePath.length - 1] : '';
    return (
        <Dialog open={open} TransitionProps={{onEnter: resetDialog, onClose: handleCancelInternal}} fullWidth={true} maxWidth="sm">
            <DialogTitle>{translate(`cmss.resources.file-explorer.directory-actions-menu.buttons.create-share-link`)} <small>({name})</small></DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {
                        url 
                        ?<Grid item xs={12}>
                            <Grid container spacing={1} alignItems={'center'}>
                                <Grid item style={{ flexGrow: 1}}>
                                    <FormControl className={classes.fullWidth} variant="outlined">
                                        <TextField
                                            inputRef={urlInputRef}
                                            value={url || ''}
                                            variant="outlined"
                                            type="text"
                                            readOnly
                                            label={translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.link`)}
                                            disabled={!url}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <IconButton variant="outlined" onClick={handleOnClickCopyLinkButton} disabled={!url}>
                                        <FileCopyIcon size="small"/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                        :<>
                            <Grid item xs={8}>
                                <FormControl className={classes.fullWidth} variant="outlined">
                                    <TextField
                                        variant="outlined"
                                        type="number"
                                        InputProps={{ inputProps: { step: 1, min: 1, max: 1000 } }}
                                        label={translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.duration`)}
                                        defaultValue={defaultTimeQuantity}
                                        {...register('expiration_time_quantity')}
                                        error={errors.expiration_time_quantity ? true : false}
                                        helperText={errors.expiration_time_quantity?.message ?? undefined}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl className={classes.fullWidth} variant="outlined">
                                <Select
                                    fullWidth
                                    defaultValue={defaultTimeUnit}
                                    {...register('expiration_time_unit')}
                                    error={errors.expiration_time_unit ? true : false}
                                >
                                    {
                                        timeUnits.map((unit) => 
                                            <MenuItem key={unit} value={unit}>{translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.time-units.${unit}`)}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            </Grid>
                        </>
                    }

                </Grid>
            </DialogContent>
            <DialogActions>
                {
                    url
                    ?<Button variant="contained" onClick={handleCancelInternal} color="primary">
                        {translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.done`)}
                    </Button>
                    :<>
                        <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleSubmit(handleOnClickCreateLinkButton)}
                        color="primary"
                        >
                            {translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.create`)}
                            {isLoading && (
                                <CircularProgress
                                    size={24}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                    }}
                                />
                            )}
                        </Button>
                        <Button onClick={handleCancelInternal} color="secondary">
                            {translate(`cmss.resources.file-explorer.directory-actions-menu.share-link.cancel`)}
                        </Button>
                    </>
                }


            </DialogActions>
        </Dialog>
    );
}

export default connect(null, {
    showNotification
})(ShareLinkDialog);